import React, { useMemo, useState } from "react";
import { Alert, Box, Paper, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { apiClient } from "../../services/api";
import { EggRoutingRule } from "../../types";
import EggRuleForm, {
  EggRuleFormData,
  formDataToPayload,
  ruleToFormData,
} from "./EggRuleForm";

const withCopySuffix = (name: string): string => {
  const suffix = " (Copia)";
  const max = 100;
  const base =
    name.length + suffix.length > max
      ? name.slice(0, max - suffix.length)
      : name;
  return base + suffix;
};

export default function EggRuleCreate() {
  const navigate = useNavigate();
  const location = useLocation();
  const duplicateFrom = (
    location.state as { duplicateFrom?: EggRoutingRule } | null
  )?.duplicateFrom;
  const isDuplicate = !!duplicateFrom;

  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const duplicateDefaults: EggRuleFormData | undefined = useMemo(() => {
    if (!duplicateFrom) return undefined;
    const base = ruleToFormData(duplicateFrom);
    return {
      ...base,
      name: withCopySuffix(base.name),
      active: false,
    };
  }, [duplicateFrom]);

  const handleSubmit = async (data: EggRuleFormData) => {
    setIsLoading(true);
    setSubmitError(null);
    try {
      await apiClient.post("/v1/egg/rules", formDataToPayload(data));
      navigate("/egg/rules");
    } catch (e) {
      console.error(e);
      setSubmitError("Errore durante la creazione della regola");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", py: 3 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
        {isDuplicate ? "Duplica regola EGG" : "Nuova regola EGG"}
      </Typography>

      <Paper sx={{ p: 3 }}>
        {isDuplicate && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Stai duplicando la regola <strong>{duplicateFrom?.name}</strong> — il
            nuovo record verrà creato come copia disattivata. Rivedi priorità,
            URL e credenziali prima di salvare.
          </Alert>
        )}
        <EggRuleForm
          defaultValues={duplicateDefaults}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          submitLabel={isDuplicate ? "Crea copia" : "Crea regola"}
          submitError={submitError}
        />
      </Paper>
    </Box>
  );
}
